import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Star } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { MovieActions } from "../components/MovieActions";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { recommendMovies } from "../lib/api";
import { MOVIES, getMovie } from "../mock/data";

const MOODS = ["Family", "Romance", "Thriller", "Comedy", "Action", "Serious drama", "Historical", "Spiritual", "Classic", "New release", "Musical", "Mystery"];
const LANGS = ["Any", "Malayalam", "Hindi", "Tamil", "Telugu", "Kannada", "Rajasthani"];
const TIMES = ["Any", "Under 2 hours", "2–2.5 hours", "Over 2.5 hours"];
const PLATFORMS = ["Any", "Netflix", "Prime Video", "JioHotstar", "SonyLIV", "aha", "Theatres"];

export default function WhatToWatch() {
  const { t, lang } = useTranslate();
  const [moods, setMoods] = useState([]);
  const [language, setLanguage] = useState("Any");
  const [time, setTime] = useState("Any");
  const [platform, setPlatform] = useState("Any");
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggleMood = (m) => setMoods((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);

  const find = async () => {
    setBusy(true); setResult(null);
    try {
      const catalog = MOVIES.map((m) => ({ id: m.id, title: m.title, language: m.language, genre: m.genre, mood: m.reviewDims ? undefined : undefined, synopsis: m.synopsis }));
      const data = await recommendMovies({
        catalog, moods, language: language === "Any" ? null : language,
        max_runtime: time === "Any" ? null : time, platform: platform === "Any" ? null : platform,
        free_text: freeText || null, reply_language: lang.name,
      });
      setResult(data);
    } catch (e) {
      setResult({ intro: t("Here are a few picks to get you started."), picks: MOVIES.slice(0, 3).map((m) => ({ id: m.id, reason: t("A standout from our catalogue.") })) });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-12 md:py-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5">
          <Sparkles className="h-4 w-4 text-gold" /><Overline className="!tracking-[0.2em]">AI Movie Discovery</Overline>
        </div>
        <h1 className="mt-6 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">
          {t("What should I ")}<span className="italic text-gradient-gold">{t("watch?")}</span>
        </h1>
        <p className="mt-4 max-w-2xl font-body text-lg text-ash">{t("Tell us the mood. Our concierge picks from the IndiasCinema catalogue and explains why each one fits.")}</p>

        <div className="mt-10 space-y-8 rounded-3xl border border-edge bg-surface/40 p-6 md:p-10">
          <div>
            <label className="font-body text-sm uppercase tracking-widest text-gold">{t("I'm in the mood for")}</label>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="mood-chips">
              {MOODS.map((m) => (
                <button key={m} onClick={() => toggleMood(m)} data-testid={`mood-${m}`}
                  className={`rounded-full border px-4 py-2 font-body text-sm transition-colors duration-200 ${moods.includes(m) ? "border-gold bg-gold text-ink" : "border-edge text-cream hover:border-gold"}`}>
                  {t(m)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Selector label="Language" value={language} setValue={setLanguage} options={LANGS} testId="wtw-language" />
            <Selector label="Time available" value={time} setValue={setTime} options={TIMES} testId="wtw-time" />
            <Selector label="Where" value={platform} setValue={setPlatform} options={PLATFORMS} testId="wtw-platform" />
          </div>

          <div>
            <label className="font-body text-sm uppercase tracking-widest text-gold">{t("Anything else? (optional)")}</label>
            <input value={freeText} onChange={(e) => setFreeText(e.target.value)} data-testid="wtw-freetext"
              onKeyDown={(e) => e.key === "Enter" && find()}
              placeholder={t("e.g. I loved Drishyam, something with a twist")}
              className="mt-3 w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
          </div>

          <button onClick={find} disabled={busy} data-testid="wtw-find"
            className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-3.5 font-body font-medium text-ink transition-transform duration-300 hover:scale-105 disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
            {busy ? t("Finding your films...") : t("Find my movie")}
          </button>
        </div>

        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-12" data-testid="wtw-results">
              <p className="mb-6 font-display text-2xl italic text-cream">{result.intro}</p>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {result.picks.map((pick, i) => { const m = getMovie(pick.id); if (!m) return null; return (
                  <motion.div key={pick.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    data-testid={`pick-${pick.id}`} className="overflow-hidden rounded-2xl border border-edge bg-surface/50">
                    <Link to={`/movie/${m.id}`}>
                      <div className="relative aspect-[2/3] overflow-hidden">
                        <img src={m.image} alt={m.title} className="h-full w-full object-cover" />
                        <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-paper/85 px-2.5 py-1 backdrop-blur">
                          <Star className="h-3.5 w-3.5 fill-gold text-gold" /><span className="font-body text-sm text-cream">{m.rating}</span>
                        </div>
                      </div>
                    </Link>
                    <div className="p-5">
                      <Link to={`/movie/${m.id}`}><div className="font-display text-2xl text-cream hover:text-gold">{m.title}</div></Link>
                      <div className="font-body text-xs text-ash">{m.language} · {m.year} · {t(m.genre)}</div>
                      <p className="mt-3 font-body text-sm leading-relaxed text-ash">“{pick.reason}”</p>
                      <div className="mt-4"><MovieActions movieId={m.id} /></div>
                    </div>
                  </motion.div>
                ); })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const Selector = ({ label, value, setValue, options, testId }) => {
  const { t } = useTranslate();
  return (
    <div>
      <label className="font-body text-sm uppercase tracking-widest text-gold">{t(label)}</label>
      <select value={value} onChange={(e) => setValue(e.target.value)} data-testid={testId}
        className="mt-3 w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream focus:border-gold focus:outline-none">
        {options.map((o) => <option key={o} value={o} className="bg-surface">{t(o)}</option>)}
      </select>
    </div>
  );
};
